import tokensCss from "../styles/tokens.css?inline";
import fontsCss from "./css/fonts.css?inline";
import skinCssUrl from "./skin.css?url";
export const SKIN_TOKENS_CSS = tokensCss;
/**
 * Version stamp for the currently-shipped skin. Carried onto every stage host element
 * (`data-skin-version`, see `runtime/stage-loader.ts`) as a forward-compat marker — a stage
 * (or a future host) can read it without depending on any private skin internals. Bump this
 * alongside a visible skin revision; it is independent of `ui_api` (the `StageContext`
 * contract version), which the skin never touches (`CLIENT_NODE_STAGE_CONTRACT.md` §7/§8).
 */
export const SKIN_VERSION = "1.0.0";
/**
 * Build-resolved URL of the extracted skin CSS asset (`skin.css`, the full vendored skin —
 * tokens through the shell-utility layer, see that file's own `@import` ordering doc
 * comment). A plain string, not I/O — safe to export from `client/ui`. The composition root
 * (`system/pwa/src/main.ts`) passes this to `@econ-v1/adapters-web`'s
 * `fetchSkinCssText()` (UI package code may not call `fetch` itself — boundary rules
 * B2/B3/N1, `client/scripts/check-boundaries.mjs`) and hands the resolved text to
 * `applySkinStyleSheet()` below.
 */
export const SKIN_CSS_URL = skinCssUrl;
let cachedSkinStyleSheet;
/**
 * Cached singleton adopted by every shadow root that carries the skin (the shell's own
 * `<node-app-shell>` root, and every per-stage shadow root `runtime/stage-loader.ts`
 * creates) — one `CSSStyleSheet` instance, shared by reference across all adopters, per
 * the constructable-stylesheets model.
 *
 * #1528 (CSS-out-of-bootstrap): unlike before, this sheet starts EMPTY — the ~83KB gzip of
 * vendored skin CSS (`skin.css`, the former `SKIN_CSS` JS template-literal concatenation)
 * is no longer inlined into this module as a JS string, so there is nothing to
 * `replaceSync` synchronously here. Callers that adopt this sheet before
 * `applySkinStyleSheet()` resolves get a real, empty `CSSStyleSheet` object; because
 * `CSSStyleSheet.replace()` mutates that SAME object in place (not a copy), every adopter —
 * whenever it attached — repaints with the real skin the moment that promise resolves. The
 * shell's own boot sequence (`system/pwa/src/main.ts`) avoids ever showing that empty
 * flash by awaiting the fetch + apply chain before inserting `<node-app-shell>` into the
 * document at all.
 */
export function getSkinStyleSheet() {
    return (cachedSkinStyleSheet ??= new CSSStyleSheet());
}
/**
 * Replaces `cssText` into `getSkinStyleSheet()`'s cached singleton. Pure DOM plumbing — no
 * I/O of its own, which is why it can live in `client/ui` even though UI package code may
 * not perform browser I/O directly. The composition root owns fetching the text (see
 * `SKIN_CSS_URL`'s doc comment above): `system/pwa/src/main.ts`'s top-level `skinReady`
 * chain is `fetchSkinCssText(SKIN_CSS_URL).then(applySkinStyleSheet)`, called exactly once
 * per page load, so no memoization is needed here — repeated calls would simply replace the
 * same singleton again.
 */
export async function applySkinStyleSheet(cssText) {
    await getSkinStyleSheet().replace(cssText);
}
// Document-level slice of the skin: tokens (the `--ds-*`/`--ink-*`/brand custom properties,
// via tokens.css's own paired `:host, :root {...}` pattern) + `@font-face` declarations only —
// no shell-chrome classes. Adopted once onto `document.adoptedStyleSheets` (see
// `system/pwa/src/main.ts`) so BOTH tokens and registered fonts inherit into every shadow
// root in the page — the shell's own, and every per-stage root — without each root needing
// its own copy. The shell shadow root keeps adopting the full `getSkinStyleSheet()` too
// (component classes still need shadow scoping); duplicate token/font rules across the two
// sheets are harmless — same cascade origin, same values, last one adopted wins ties.
//
// #1528 (CSS-out-of-bootstrap): this slice stays a synchronous `?inline` JS string — NOT
// moved behind the async `SKIN_CSS_URL` fetch — because it carries `fonts.css`'s FOUC guard
// (`html:not(.page-ready) body{visibility:hidden!important}`, see `markSkinPageReady`'s own
// doc comment below) and `@font-face` registrations that must be live before first paint,
// not after a network round-trip. Its gzip cost (~2KB) is negligible next to the ~83KB the
// rest of the skin carried.
const GLOBAL_SKIN_CSS = [tokensCss, fontsCss].join("\n");
let cachedSkinGlobalSheet;
export function getSkinGlobalSheet() {
    if (cachedSkinGlobalSheet === undefined) {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(GLOBAL_SKIN_CSS);
        cachedSkinGlobalSheet = sheet;
    }
    return cachedSkinGlobalSheet;
}
/**
 * `fonts.css` line 1 (`html:not(.page-ready) body{visibility:hidden!important}`) is a FOUC
 * guard from the vendored design (`.node-design/original-skin/core/ui/styles/fonts.css`),
 * always paired there with a boot script (`core/boot/page-ready.js`) that stamps
 * `document.documentElement.classList.add('page-ready')` once the DOM is ready (with a 3s
 * failsafe). That selector only ever matched inside a real `<html>/<body>` — while `fonts.css`
 * lived solely inside the shell's shadow-scoped sheet (no `<html>`/`<body>` inside a shadow
 * tree), the rule was permanently inert and the pairing boot script was never needed. Adopting
 * `fontsCss` onto `document.adoptedStyleSheets` (`getSkinGlobalSheet`, above) makes the guard
 * live for the first time in this app — without this call nothing would ever add
 * `.page-ready`, and `<body>` would stay `visibility: hidden` forever. Port the pairing boot
 * behavior verbatim so the guard resolves the way the source design always intended.
 */
export function markSkinPageReady(target = document) {
    const stamp = () => target.documentElement.classList.add("page-ready");
    if (target.readyState === "loading") {
        target.addEventListener("DOMContentLoaded", stamp, { once: true });
    }
    else {
        stamp();
    }
    // Failsafe: never leave the page hidden if DOMContentLoaded stalls (mirrors the vendored
    // core/boot/page-ready.js exactly).
    setTimeout(stamp, 3000);
}
//# sourceMappingURL=skin-stylesheet.js.map